import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { fileHelpers } from "../../../features/files"
import { TableName } from "../../../tables"
import { Currency } from "../../../tables/types/types"
import { firstItem } from "../../../utils/listUtils"
import { PaymentStatus } from "../../operations"
import { useExpenseActions } from "../actions/expenseActions"
import { useExpenseBuilders } from "../builders/expenseBuilders"
import { useExpenseHooks } from "../hooks/expenseHooks"
import { useExpenseLoaders } from "../loaders/expenseLoaders"
import { useTicketLoaders } from "../loaders/ticketLoaders"
import { Ticket } from "../state/types"
import { useTicketService } from "./ticketService"

export const useExpenseService = () => {
    const { expenseTicketSubPath, salaryTicketSubPath, getFileExtension } = fileHelpers
    const { isSalary } = useExpenseHooks()
    const { generateCode } = useDataTypesHooks()

    const { buildExpense } = useExpenseBuilders()
    
    const expenseLoaders = useExpenseLoaders()
    const ticketLoaders = useTicketLoaders()

    const expenseActions = useExpenseActions()
    const { createTicket, updateTicket, removeTicket } = useTicketService()

    const createExpense = async (
        expenseData: Data2,
        ticketData: Data2,
        taxItemsData: Data2[] = []
    ) => {
        const code = await generateCode(TableName.EXPENSES)
        const newExpenseData: Data2 = {
            ...expenseData,
            code,
            currency: Currency.ARS,
            paymentStatus: PaymentStatus.PENDING
        }

        const expense = buildExpense(newExpenseData)
        const savedExpense = await expenseActions.saveExpense(expense)

        const newTicketData: Data2 = {
            ...ticketData,
            creationDate: expenseData.date,
            parentTable: TableName.EXPENSES,
            parentId: savedExpense.id!
        }

        const ext = ticketData.ticketFile instanceof File ? getFileExtension(ticketData.ticketFile) : 'pdf'
        const fileSubPath = (ticket: Ticket) => isSalary(savedExpense) ?
            salaryTicketSubPath(ticket, expense.code, ext) :
            expenseTicketSubPath(ticket, expense.code, ext)
        await createTicket(newTicketData, fileSubPath, taxItemsData)
                
        return savedExpense
    }

    const updateExpense = async (
        expenseData: Data2,
        ticketData: Data2
    ) => {
        const { loadExpense } = expenseLoaders()
        const loadedExpense = await loadExpense(expenseData.id! as string)

        const newExpenseData: Data2 = {
            ...loadedExpense,
            ...expenseData
        }
        
        const expense = buildExpense(newExpenseData)
        const savedExpense = await expenseActions.saveExpense(expense)

        const { loadParentTickets } = ticketLoaders()
        const loadedTickets = await loadParentTickets(expense.id)
        const loadedTicket = firstItem(loadedTickets)
        const newTicketData = {
            ...ticketData,
            id: loadedTicket!.id
        }

        const ext = ticketData.ticketFile instanceof File ? getFileExtension(ticketData.ticketFile) : 'pdf'
        const fileSubPath = (ticket: Ticket) => isSalary(savedExpense) ?
            salaryTicketSubPath(ticket, expense.code, ext) :
            expenseTicketSubPath(ticket, expense.code, ext)
        await updateTicket(newTicketData, fileSubPath)

        return savedExpense
    }

    const removeExpense = async (id: string) => {
        await expenseActions.removeExpense(id)

        const { loadParentTickets } = ticketLoaders()
        const loadedTickets = await loadParentTickets(id)
        loadedTickets.forEach(async ticket => {
            await removeTicket(ticket.id)
        })
    }

    const archiveExpense = async (expenseId: string) => {
        const { loadExpense } = expenseLoaders()
        const expense = await loadExpense(expenseId, true)
        const archivedExpense = buildExpense({
            ...expense,
            archived: true
        })
        return expenseActions.saveExpense(archivedExpense)
    }

    return {
        createExpense,
        updateExpense,
        removeExpense,
        archiveExpense
    }
}
